import { useState, useEffect, useCallback } from "react";
import ResourceSearchResult from "../../../OBJRep/ResourceSearchResult";

function usePaginatedResources(
  loadFirstPageCallback,
  loadNextPageCallback,
  onResourcesSearchResultChange,
  initialPage,
  initialHasNextPage
) {
  const [page, setPage] = useState(initialPage || 0);
  const [loading, setLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(initialHasNextPage || false);
  const [error, setError] = useState(null);

  /**
   * Loads the initial resources based on the provided filter parameters.
   * It sets the loading state, resets the error state, and calls the callback to fetch the first page of resources.
   * On success, it updates the hasNextPage state and calls the onResourcesSearchResultChange with the initial data.
   * @param {Object} filterParams - The parameters to filter the resources.
   */
  const loadInitialResources = useCallback(
    async (params) => {
      setLoading(true);
      setError(null);
      setPage(0);
      try {
        const initialData = await loadFirstPageCallback(params);
        setHasNextPage(initialData?.hasNextPage || false);
        onResourcesSearchResultChange(initialData || ResourceSearchResult());
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [onResourcesSearchResultChange]
  );

  /**
   * Loads more resources based on the current page and filter parameters.
   * It checks if there are more pages to load and if the loading state is false.
   * If so, it increments the page number, fetches the next page of resources,
   * updates the hasNextPage state, and appends the new resources to the existing search result.
   * On error, it sets the error state.
   * @param {Object} filterParams - The parameters to filter the resources.
   */
  const loadMoreResources = useCallback(
    async (params) => {
      if (!hasNextPage || loading) return;
      setLoading(true);
      setError(null);
      const nextPage = page + 1;
      try {
        const nextData = await loadNextPageCallback(nextPage, params);
        setHasNextPage(nextData?.hasNextPage || false);
        setPage(nextPage);
        onResourcesSearchResultChange((prevResourceSearchResult) => {
          const searchResult = ResourceSearchResult(
            [...prevResourceSearchResult?.resources, ...nextData.resources],
            nextData.isExactMatch,
            nextData.currentPage,
            nextData.hasNextPage
          );
          return searchResult;
        });
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [
      hasNextPage,
      loading,
      loadNextPageCallback,
      onResourcesSearchResultChange,
      page,
    ]
  );

  return {
    loading,
    error,
    hasNextPage,
    loadMoreResources,
    loadInitialResources,
  };
}

export default usePaginatedResources;
